import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import { Target, Zap, Eye, Lightbulb } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-900 mb-6">
            About BrainyDo
          </h1>
          <p className="text-xl text-slate-600">
            We're on a mission to transform how businesses manage WhatsApp communication
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
              Our Story
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              BrainyDo was born from a simple observation: businesses are losing opportunities because important WhatsApp messages get lost in endless chats. Sales leads, client requests, and follow-ups slip through the cracks.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              We built BrainyDo to solve this problem using AI. Now, businesses can automatically capture, organize, and act on every WhatsApp message that matters.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              BrainyDo is developed by <strong>WebTeam Private Limited</strong>, a forward-thinking technology company dedicated to transforming how businesses communicate and manage their workflows. With our expertise in AI and automation, we're committed to building products that solve real business problems.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <Eye className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Our Vision
              </h3>
              <p className="text-slate-600 leading-relaxed">
                To transform WhatsApp into a powerful execution engine for businesses worldwide. A platform where no opportunity is missed, no follow-up is forgotten, and every message drives action.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <Target className="w-12 h-12 text-green-500 mb-4" />
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Our Mission
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Reduce missed opportunities</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Improve follow-up consistency</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-green-500 font-bold">✓</span>
                  <span>Bring clarity to communication</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why BrainyDo */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4">
              Why choose BrainyDo?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Built for Real Usage",
                desc: "Created by people who understand the challenges businesses face with WhatsApp communication"
              },
              {
                icon: Lightbulb,
                title: "AI-Powered",
                desc: "Powered by cutting-edge AI to automatically classify, extract, and act on messages"
              },
              {
                icon: Target,
                title: "Simple & Focused",
                desc: "Designed with simplicity in mind — no complexity, just the features you need"
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <item.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
